import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { CardIcons } from "./CardIcons";
import ProjectPreviewPanel from "./ProjectPreviewPanel";
import { GoLinkExternal, GoLock, GoMarkGithub } from "react-icons/go";
import styled from "styled-components";
import Collapse from "react-bootstrap/Collapse";
import ImageCarousel from "./ProjectImageCarousel";

const GithubIcon = styled(GoMarkGithub)({
	fontSize: "2rem",
	marginLeft: "1rem",
	transitionDuration: 1,
	"&:hover": {
		color: (props) => (props.shade ? "white" : "black"),
		transitionDuration: 1,
	},
});
const LockIcon = styled(GoLock)({
	fontSize: "1.8rem",
	marginLeft: "1rem",
});
const PlayIcon = styled(GoLinkExternal)({
	fontSize: "1.8rem",
	marginLeft: "1rem",
	transitionDuration: 1,
	"&:hover": {
		color: (props) => (props.shade ? "white" : "black"),
		transitionDuration: 1,
	},
});

const ProjectCard = (props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [buttonText, setButtonText] = useState("Show");

	const [linkTitle, setLinkTitle] = useState("");
	const [linkPresent, setLinkPresent] = useState(false);
	const [playTitle, setPlayTitle] = useState("");
	const [playPresent, setPlayPresent] = useState(false);

	const normalizeExternalUrl = (url) => {
		if (typeof url !== "string") {
			return "";
		}

		const trimmedUrl = url.trim();
		if (!trimmedUrl) {
			return "";
		}

		if (/^https?:\/\//i.test(trimmedUrl)) {
			return trimmedUrl;
		}

		return `https://${trimmedUrl}`;
	};

	useEffect(() => {
		setLinkTitle(props.link || "");
		setLinkPresent(Boolean(props.link));
		const normalizedPlayUrl = normalizeExternalUrl(props.play);
		setPlayTitle(normalizedPlayUrl);
		setPlayPresent(Boolean(normalizedPlayUrl));
	}, [props.link, props.play]);

	return (
		<Card
			className={
				props.shade
					? "mt-3 p-2 pt-3 bg-dark text-light"
					: "mt-3 p-2 pt-3 bg-light text-dark"
			}
		>
			<Card.Body className="d-flex align-items-center justify-content-between">
				<div className="d-flex align-items-center">
					<Card.Title className="text-left text-blue mb-0">
						{props.label}
					</Card.Title>
					{linkPresent && (
						<Card.Link
							href={linkTitle}
							rel="noopener noreferrer"
							target="_blank"
							className="d-inline-flex align-items-center"
						>
							<GithubIcon shade={props.shade} />
						</Card.Link>
					)}
					{!linkPresent && (
						<OverlayTrigger
							placement="top"
							overlay={<Tooltip id="private-repo-tooltip">private...</Tooltip>}
						>
							<span className="d-inline-flex align-items-center">
								<LockIcon />
							</span>
						</OverlayTrigger>
					)}
					{playPresent && (
						<Card.Link
							href={playTitle}
							rel="noopener noreferrer"
							target="_blank"
							className="d-inline-flex align-items-center ms-0"
						>
							<OverlayTrigger
								placement="top"
								overlay={<Tooltip id="live-site-tooltip">Live website</Tooltip>}
							>
								<span className="d-inline-flex align-items-center">
									<PlayIcon shade={props.shade} />
								</span>
							</OverlayTrigger>
						</Card.Link>
					)}
				</div>

				<Button
					as={motion.button}
					whileTap={{ scale: 0.95 }}
					className="d-none d-md-block w-25 shadow shbtn"
					onClick={() => {
						setButtonText(isOpen ? "Show" : "Hide");
						setIsOpen(!isOpen);
					}}
				>
					{buttonText}
				</Button>
			</Card.Body>
			<Button
				as={motion.button}
				whileTap={{ scale: 0.95 }}
				className="d-md-none mb-2  w-100 shadow shbtn"
				onClick={() => {
					setButtonText(isOpen ? "Show" : "Hide");
					setIsOpen(!isOpen);
				}}
				aria-controls="example-collapse-text"
				aria-expanded={isOpen}
			>
				{buttonText}
			</Button>

			<Collapse in={isOpen}>
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.5 }}
				>
					<Card.Footer
						className={
							props.shade
								? "mt-3 bg-dark footer-open"
								: "mt-3 bg-light footer-open"
						}
					>
						<Card.Text className="subfont">
							{props.description}
						</Card.Text>
						{props.details && props.details !== props.description && (
							<ProjectPreviewPanel
								shade={props.shade}
								details={props.details}
							/>
						)}
						<ImageCarousel images={props.images} />
					</Card.Footer>
				</motion.div>
			</Collapse>
			<Card.Footer
				className={
					props.shade
						? "mt-3 bg-dark footer-close"
						: "mt-3 bg-light footer-close"
				}
			>
				<CardIcons
					languages={props.languages || []}
					isLoading={Boolean(props.languagesLoading)}
				/>
			</Card.Footer>
		</Card>
	);
};

export default ProjectCard;
